/**
 * One consistent header for every app-builder resource section (tables, forms,
 * datasets, processes, automations, scheduled rules, …): an icon +
 * title + an optional count chip + a right-aligned action.
 *
 * Before this, each section hand-rolled its own header — some with icons, some
 * without; mismatched action buttons; no counts — which is what made the app
 * detail screen feel like a pile of unrelated tables ("appiccicato"). Routing
 * every section through one component gives the whole screen a single rhythm.
 * Lives in its own module (not AppDetailPage) so both the page sections and the
 * standalone `ScheduledRulesSection` can import it without a circular import.
 */
import type { LucideIcon } from "lucide-react";
import type * as React from "react";

export function SectionHeader({
  icon: Icon,
  title,
  count,
  action,
}: {
  icon: LucideIcon;
  title: string;
  /** Item count rendered as a subtle chip after the title (hidden when 0). */
  count?: number;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        {title}
        {count !== undefined && count > 0 && (
          <span className="rounded-full bg-elevated px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
      </h3>
      {action}
    </div>
  );
}
